/**
 * Chaos Schema List Hook
 *
 * FERP CRITICAL: Browse and select chaos documents for conversion
 * Domain-agnostic chaos document repository access
 *
 * Principles:
 * - All chaos documents from backend (ChaosDocumentRepository)
 * - Filter by domain, complexity, and status
 * - Repository pattern strict enforcement (no direct API calls)
 * - Immutable audit trail for document access
 * - Life-critical document tracking
 */

import { useState, useCallback, useEffect } from 'react';
import { useCurrentTenant } from './useCurrentTenant';
import { chaosDocumentRepository, type ChaosDocument as RepoChaosDocument, type ChaosDocumentDetails as RepoChaosDocumentDetails } from '../data/repositories';
import { DocumentStatus } from '../data/repositories/ChaosDocumentRepository';

// ============================================================
// TypeScript Types (Re-exported from Repository)
// ============================================================
// FERP PRINCIPLE: Types defined in repository layer, not duplicated in hooks

// Convenience aliases for backward compatibility
export interface ChaosDocument {
  document_id: string;
  filename: string;
  file_type: string;
  size_bytes: number;
  upload_status: string;
  detected_domain: string;
  detected_complexity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  created_at: string;
  created_by: string;
  conversion_count: number;
  last_conversion_at?: string;
}

export interface ConversionHistoryEntry {
  extraction_id: string;
  converted_at: string;
  confidence_score: number;
  template_id: string;
  template_version: string;
}

export interface ChaosDocumentDetails {
  document_id: string;
  filename: string;
  file_type: string;
  size_bytes: number;
  upload_status: string;
  detected_domain: string;
  detected_complexity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  detected_frameworks: string[];
  estimated_field_count: number;
  estimated_primitive_count: number;
  created_at: string;
  created_by: string;
  conversion_history: ConversionHistoryEntry[];
  raw_content_preview: string;
}

// ============================================================
// Hook Implementation
// ============================================================

export function useChaosSchemaList() {
  const { tenantId } = useCurrentTenant();

  const [documents, setDocuments] = useState<ChaosDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * List all chaos documents
   * REPOSITORY PATTERN: Uses chaosDocumentRepository.listDocuments()
   * Optionally filter by domain and status
   */
  const listDocuments = useCallback(async (
    domain?: string,
    status?: string
  ): Promise<ChaosDocument[]> => {
    setIsLoading(true);
    setError(null);

    try {
      // Guard against null tenantId (may be null before tenant context loads)
      if (!tenantId) {
        setDocuments([]);
        return [];
      }

      const statusFilter = status as DocumentStatus | undefined;
      const repoDocuments = await chaosDocumentRepository.listDocuments(tenantId, statusFilter);

      // Map repository response to hook's ChaosDocument type
      const docs: ChaosDocument[] = repoDocuments.map(doc => ({
        document_id: doc.documentId,
        filename: doc.filename,
        file_type: doc.mimeType,
        size_bytes: doc.fileSize,
        upload_status: doc.status,
        detected_domain: 'unknown',
        detected_complexity: 'MEDIUM' as any,
        created_at: doc.uploadedAt,
        created_by: doc.uploadedBy,
        conversion_count: doc.latestConversion ? 1 : 0,
        last_conversion_at: doc.latestConversion?.convertedAt
      }));

      // Filter by domain if specified (client-side filtering)
      const filteredDocs = domain
        ? docs.filter(doc => doc.detected_domain === domain)
        : docs;

      setDocuments(filteredDocs);
      return filteredDocs;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to list chaos documents');
      setError(error);
      throw error;

    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  /**
   * Get detailed information about a chaos document
   * REPOSITORY PATTERN: Uses chaosDocumentRepository.getDocumentDetails()
   * Including conversion history and content preview
   */
  const getDocumentDetails = useCallback(async (
    documentId: string
  ): Promise<ChaosDocumentDetails> => {
    setIsLoading(true);
    setError(null);

    try {
      // Guard against null tenantId
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      const repoDetails = await chaosDocumentRepository.getDocumentDetails(tenantId, documentId);

      // Guard against null repository response
      if (!repoDetails) {
        throw new Error('Document details not found');
      }

      // Map repository response to hook's ChaosDocumentDetails type
      const details: ChaosDocumentDetails = {
        document_id: repoDetails.documentId,
        filename: repoDetails.filename,
        file_type: repoDetails.mimeType,
        size_bytes: repoDetails.fileSize,
        upload_status: repoDetails.status,
        detected_domain: repoDetails.extractedMetadata?.detectedDomain || 'unknown',
        detected_complexity: (repoDetails.extractedMetadata?.complexity as any) || 'MEDIUM',
        detected_frameworks: [],
        estimated_field_count: repoDetails.extractedMetadata?.fieldCount || 0,
        estimated_primitive_count: repoDetails.extractedMetadata?.sectionCount || 0,
        created_at: repoDetails.uploadedAt,
        created_by: repoDetails.uploadedBy,
        conversion_history: (repoDetails.conversionHistory || []).map(c => ({
          extraction_id: c.conversionId,
          converted_at: c.convertedAt,
          confidence_score: c.confidence,
          template_id: c.templateId ?? '',
          template_version: c.templateVersion ?? '',
        })),
        raw_content_preview: repoDetails.extractedMetadata?.textPreview || ''
      };

      return details;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get document details');
      setError(error);
      throw error;

    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  /**
   * Delete chaos document
   * REPOSITORY PATTERN: Uses chaosDocumentRepository.deleteDocument()
   * CRITICAL: Soft delete with audit trail
   */
  const deleteDocument = useCallback(async (
    documentId: string
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Guard against null tenantId
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      await chaosDocumentRepository.deleteDocument(tenantId, documentId);

      // Remove from local state
      setDocuments(prev => prev.filter(doc => doc.document_id !== documentId));

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete document');
      setError(error);
      throw error;

    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  /**
   * Filter documents by domain
   */
  const filterByDomain = useCallback((domain: string): ChaosDocument[] => {
    return documents.filter(doc => doc.detected_domain === domain);
  }, [documents]);

  /**
   * Filter documents by complexity
   */
  const filterByComplexity = useCallback((
    complexity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  ): ChaosDocument[] => {
    return documents.filter(doc => doc.detected_complexity === complexity);
  }, [documents]);

  /**
   * Get documents that have been converted
   */
  const getConvertedDocuments = useCallback((): ChaosDocument[] => {
    return documents.filter(doc => doc.conversion_count > 0);
  }, [documents]);

  /**
   * Get documents that have not been converted yet
   */
  const getUnconvertedDocuments = useCallback((): ChaosDocument[] => {
    return documents.filter(doc => doc.conversion_count === 0);
  }, [documents]);

  // Load documents on mount
  useEffect(() => {
    listDocuments();
  }, [listDocuments]);

  return {
    // State
    documents,
    isLoading,
    error,

    // Actions
    listDocuments,
    getDocumentDetails,
    deleteDocument,

    // Filters
    filterByDomain,
    filterByComplexity,
    getConvertedDocuments,
    getUnconvertedDocuments
  };
}

/**
 * Specialized hook for chaos document statistics
 */
export function useChaosDocumentStats(documents: ChaosDocument[]) {
  const totalDocuments = documents.length;
  const convertedDocuments = documents.filter(d => d.conversion_count > 0).length;
  const unconvertedDocuments = documents.filter(d => d.conversion_count === 0).length;

  const byDomain = documents.reduce((acc, doc) => {
    acc[doc.detected_domain] = (acc[doc.detected_domain] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const byComplexity = documents.reduce((acc, doc) => {
    acc[doc.detected_complexity] = (acc[doc.detected_complexity] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const averageConversions = documents.length > 0
    ? documents.reduce((sum, doc) => sum + doc.conversion_count, 0) / documents.length
    : 0;

  return {
    totalDocuments,
    convertedDocuments,
    unconvertedDocuments,
    conversionRate: totalDocuments > 0 ? (convertedDocuments / totalDocuments) * 100 : 0,
    byDomain,
    byComplexity,
    averageConversions
  };
}
